import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentTenant } from '@/contexts/UserContext';
import { normalizeTenantId } from '@/lib/tenantUtils';

const TIMEOUT_MS = 2000;
const CACHE_KEY = 'home-destination-v1';

const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

let memory = {};
const inflight = new Map();

function cacheKeyFor(tenant) {
  return normalizeTenantId(tenant) || (tenant && String(tenant).trim()) || '_';
}

export function isHomePath(pathname) {
  if (!pathname) return false;
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return path === '/Home';
}

function readSessionStore() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function readCachedRedirect(tenantKey) {
  const store = readSessionStore();
  if (store && Object.prototype.hasOwnProperty.call(store, tenantKey)) {
    memory[tenantKey] = store[tenantKey] ?? null;
    return memory[tenantKey];
  }
  if (store) {
    // sessionStorage exists but not for this tenant — do not trust a stale memory hit
    delete memory[tenantKey];
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(memory, tenantKey)) {
    return memory[tenantKey];
  }
  return undefined;
}

function writeCachedRedirect(tenantKey, redirectTo) {
  memory[tenantKey] = redirectTo;
  try {
    const store = readSessionStore() || {};
    store[tenantKey] = redirectTo;
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(store));
  } catch {
    /* sessionStorage may be unavailable */
  }
}

export function clearHomeDestinationCache() {
  memory = {};
  inflight.clear();
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** Read `home` from GET /api/market/onboarding. Do not reimplement the live-account rule. */
export function homeRedirectFromOnboarding(data) {
  const home = data?.home;
  if (home == null || typeof home !== 'object') return null;
  return home.redirect_to === '/Market' ? '/Market' : null;
}

function fetchHomeDestination(tenant, tenantKey) {
  const existing = inflight.get(tenantKey);
  if (existing) return existing;

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  const qs = tenant ? `?tenant_id=${encodeURIComponent(tenant)}` : '';

  const promise = fetch(`${API_BASE_URL}/api/market/onboarding${qs}`, {
    credentials: 'include',
    signal: controller.signal,
  })
    .then((res) => {
      if (!res.ok) throw new Error(`onboarding ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const next = homeRedirectFromOnboarding(data);
      writeCachedRedirect(tenantKey, next);
      return next;
    })
    .finally(() => {
      window.clearTimeout(timer);
      inflight.delete(tenantKey);
    });

  inflight.set(tenantKey, promise);
  return promise;
}

export function useHomeDestination() {
  const { pathname } = useLocation();
  const tenant = useCurrentTenant();
  const tenantKey = cacheKeyFor(tenant);
  const onHome = isHomePath(pathname);
  const cached = onHome ? readCachedRedirect(tenantKey) : undefined;

  const [ticket, setTicket] = useState(() => ({
    path: pathname,
    key: tenantKey,
    loading: onHome && cached === undefined,
    redirectTo: cached === undefined ? null : cached,
  }));

  const sameEntry = ticket.path === pathname && ticket.key === tenantKey;
  const loading = onHome && (sameEntry ? ticket.loading : cached === undefined);
  const redirectTo = sameEntry ? ticket.redirectTo : (cached === undefined ? null : cached);

  useEffect(() => {
    if (!onHome) return undefined;

    const hit = readCachedRedirect(tenantKey);
    if (hit !== undefined) {
      setTicket({ path: pathname, key: tenantKey, loading: false, redirectTo: hit });
      return undefined;
    }

    let cancelled = false;
    setTicket({ path: pathname, key: tenantKey, loading: true, redirectTo: null });

    fetchHomeDestination(tenant, tenantKey)
      .then((next) => {
        if (cancelled) return;
        setTicket({ path: pathname, key: tenantKey, loading: false, redirectTo: next });
      })
      .catch(() => {
        if (cancelled) return;
        setTicket({ path: pathname, key: tenantKey, loading: false, redirectTo: null });
      });

    return () => {
      cancelled = true;
    };
  }, [onHome, pathname, tenantKey, tenant]);

  return { loading, redirectTo };
}
